import React, { memo } from 'react';
import {
    Box,
    Typography,
    useMediaQuery,
    Link,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import rehypeHighlight from 'rehype-highlight';
import rehypeRaw from 'rehype-raw';
import rehypeSlug from 'rehype-slug';
import rehypeAutolinkHeadings from 'rehype-autolink-headings';
import remarkGfm from 'remark-gfm';
import { Helmet } from 'react-helmet';
import { useTheme } from '@mui/material/styles';
import 'highlight.js/styles/github.css';

const MarkDownRenderer = ({ title = '', markDownText = '' }) => {
    const theme = useTheme();
    const isMedium = useMediaQuery(theme.breakpoints.down('md'));

    return (
        <>
            <Helmet>
                <title>{title ? `${title} - HawkClient` : 'HawkClient'}</title>
                {title && <meta name="description" content={title} />}
            </Helmet>

            <Box sx={{ px: isMedium ? 3 : 10, pt: 3 }}>
                <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[
                        rehypeRaw,
                        rehypeHighlight,
                        rehypeSlug,
                        [
                            rehypeAutolinkHeadings,
                            {
                                behavior: 'wrap',
                                properties: {
                                    style: 'text-decoration: none; color: inherit;',
                                },
                            },
                        ],
                    ]}
                    components={{
                        h1: props => <Typography variant="h4" gutterBottom {...props} />,
                        h2: props => <Typography variant="h5" gutterBottom {...props} />,
                        h3: props => <Typography variant="h6" gutterBottom {...props} />,
                        h4: props => (
                            <Typography variant="subtitle1" fontWeight="bold" gutterBottom {...props} />
                        ),

                        p: props => <Typography variant="body1" sx={{ mb: 1.2 }} {...props} />,

                        ul: props => <Box component="ul" sx={{ pl: 3, mb: 1 }} {...props} />,
                        ol: props => <Box component="ol" sx={{ pl: 3, mb: 1 }} {...props} />,
                        li: props => (
                            <Typography component="li" variant="body1" sx={{ mb: 0.5 }} {...props} />
                        ),

                        blockquote: props => (
                            <Box
                                sx={{
                                    borderLeft: `4px solid ${theme.palette.primary.main}`,
                                    pl: 2,
                                    my: 2,
                                    color: theme.palette.text.secondary,
                                    fontStyle: 'italic',
                                }}
                                {...props}
                            />
                        ),

                        code: ({ inline, children, ...props }) =>
                            inline ? (
                                <Box
                                    component="code"
                                    sx={{
                                        backgroundColor: theme.palette.action.hover,
                                        px: 0.6,
                                        py: 0.2,
                                        borderRadius: 0.5,
                                        fontFamily: 'monospace',
                                    }}
                                    {...props}
                                >
                                    {children}
                                </Box>
                            ) : (
                                <Box
                                    component="pre"
                                    sx={{
                                        backgroundColor:
                                            theme.palette.mode === 'dark'
                                                ? '#1e1e1e'
                                                : theme.palette.grey[200],
                                        p: 2,
                                        borderRadius: 1,
                                        overflowX: 'auto',
                                        mb: 2,
                                    }}
                                >
                                    <code {...props}>{children}</code>
                                </Box>
                            ),

                        a: ({ href, children, ...props }) => (
                            <Link href={href} {...props}>
                                {children}
                            </Link>
                        ),

                        img: ({ src, alt }) => (
                            <Box
                                component="img"
                                src={src}
                                alt={alt}
                                sx={{
                                    maxWidth: '100%',
                                    borderRadius: 1,
                                    my: 2,
                                    boxShadow: 1,
                                }}
                            />
                        ),

                        table: props => <Table size="small" sx={{ my: 2 }} {...props} />,
                        thead: props => <TableHead {...props} />,
                        tbody: props => <TableBody {...props} />,
                        tr: props => <TableRow {...props} />,
                        th: props => <TableCell sx={{ fontWeight: 'bold' }} {...props} />,
                        td: props => <TableCell {...props} />,
                    }}
                >
                    {markDownText || 'No content available.'}
                </ReactMarkdown>
            </Box>
        </>
    );
};

export default memo(MarkDownRenderer);
